import { memo } from 'react';

import { cn } from '@veraclins-dev/utils';

import { type ComponentPropsWithoutColor } from '../types';

import { extractStyleProps } from './utils/variants/styles';
import {
  type TypographyVariants,
  typographyVariants,
} from './utils/variants/typography';

// Type definitions
type Variant = NonNullable<TypographyVariants['variant']>;

type TypographyHeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type TypographyBlockElement = TypographyHeadingElement | 'p';
type TypographyElement = TypographyBlockElement | 'span';

const variantMapping: Record<Variant, TypographyElement> = {
  hero: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'p',
  subtitle2: 'p',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  overline: 'span',
  stat: 'span',
  'stat-sm': 'span',
  small: 'span',
  inherit: 'p',
};

/**
 * Props for the Typography component.
 * @param {TypographyElement} [props.component] - The HTML element to render. Defaults to the
 *   variant's element. Pass a heading element to set the heading level apart from the visual size.
 * @param {React.ReactNode} props.children - The content to render inside the Typography component.
 * @param {string} [props.className] - Additional CSS classes to apply to the component.
 * @param {string} [props.variant='body2'] - The typography variant to apply, controlling font size, weight,
 */
type TypographyProps = ComponentPropsWithoutColor<TypographyBlockElement> & {
  children?: React.ReactNode;
  className?: string;
  component?: TypographyElement;
} & TypographyVariants;

/**
 * A versatile Typography component for rendering text with customizable styles, including variant-based
 * typography (e.g., headings, body text, captions), color schemes, text alignment, and spacing options.
 * The component is memoized for performance optimization. Heading semantics come from the rendered element.
 */
function Base({
  variant = 'body2',
  family = 'inherit',
  align = 'inherit',
  gutterBottom = false,
  noWrap = false,
  lineClamp,
  className,
  children,
  component,
  ...props
}: TypographyProps) {
  const Component = component ?? variantMapping[variant] ?? 'p';
  const { styleProps, others } = extractStyleProps(props);

  return (
    <Component
      data-slot="typography"
      className={cn(
        typographyVariants({
          variant,
          family,
          align,
          gutterBottom,
          noWrap: lineClamp ? false : noWrap,
          lineClamp,
          ...styleProps,
          className,
        }),
      )}
      title={
        noWrap && !lineClamp && typeof children === 'string'
          ? children
          : undefined
      }
      {...others}
    >
      {children}
    </Component>
  );
}

/**
 * A versatile component for rendering text with semantic, variant-based styles.
 * Covers headings, body, captions and more, with color, alignment, and spacing
 * options. Memoized for performance.
 *
 * `variant` sets the visual size. The rendered element sets the meaning. A
 * heading variant renders its own heading element by default, so
 * `variant="h2"` is an `<h2>`. When the page outline needs a different level
 * than the size, pass the level as `component`: `variant="h4" component="h2"`
 * renders an `<h2>` that looks like `h4`. Pick the level from the page outline
 * and the variant from the design, and never skip a level to get a smaller size.
 *
 * @component
 * @param {Object} props - The properties for the Typography component.
 * @param {string} [props.variant='body2'] - The typography variant to apply, controlling font size, weight,
 *   and line height. Options include:
 *   - `h1`, `h2`, `h3`, `h4`, `h5`, `h6`: Heading styles with varying sizes and weights.
 *   - `subtitle1`, `subtitle2`: Supporting text beside a heading. Renders a <p>
 *     and stays out of the document outline.
 *   - `body1`, `body2`: Body text styles for standard content.
 *   - `caption`: Small text for annotations or secondary information.
 *   - `overline`: Uppercase text with wide tracking for labels.
 *   - `inherit`: Inherits the parent typography styles and renders a <p> element.
 * @param {string} [props.align='inherit'] - Text alignment. Options include:
 *   - `inherit`: Inherits the parent alignment.
 *   - `left`: Left-aligned text.
 *   - `center`: Centered text.
 *   - `right`: Right-aligned text.
 *   - `justify`: Justified text.
 *   - `start`: Aligned to the start edge of the reading direction (left in LTR, right in RTL).
 *   - `end`: Aligned to the end edge of the reading direction (right in LTR, left in RTL).
 * @param {boolean} [props.gutterBottom=false] - Adds bottom margin (`mb-4`) for spacing below the text.
 * @param {boolean} [props.noWrap=false] - Prevents text wrapping, applying truncation (`truncate`) for overflow.
 *   When `children` is a plain string, the full text is also set as the native `title` tooltip so a
 *   truncated line can still be read. Pass `title={undefined}` to opt out, for example when the text
 *   already sits inside a `Tooltip`.
 * @param {1 | 2 | 3 | 4} [props.lineClamp] - Cuts the text to this many lines and ends it with an ellipsis
 *   (`line-clamp-{n}`). When both `lineClamp` and `noWrap` are set, `lineClamp` wins and `noWrap` is
 *   ignored, so the text clamps to `lineClamp` lines and gets no `title` tooltip. The clamp sets
 *   `display: -webkit-box`, so an inline variant (`caption`, `overline`, `small`) becomes block-level.
 * @param {string} [props.component] - The element to render: `h1` to `h6`, `p` or `span`. Defaults
 *   to the variant's element (`h1` for `hero` and `h1`, `p` for body and subtitle variants, `span`
 *   for `caption`, `overline`, `small` and the stat variants). Only a heading element is announced
 *   as a heading, so `variant="h3" component="p"` renders a paragraph styled as `h3`.
 * @param {string} [props.className] - Additional CSS classes to apply to the component.
 * @param {React.ReactNode} props.children - The content to render inside the Typography component.
 * @param {...any} props - Additional props are spread onto the rendered HTML element.
 * @returns {JSX.Element} The rendered Typography component.
 *
 * @example
 * ```jsx
 * <Typography variant="h1" gutterBottom>
 *   Welcome to Our Website
 * </Typography>
 * ```
 *
 * @example
 * ```jsx
 * // A card grid under the page's h1: the level is 2, the size is h4.
 * <Typography variant="h4" component="h2">
 *   Recent invoices
 * </Typography>
 * ```
 *
 * @remarks
 * - **Variant Mapping**: The component maps typography variants to HTML elements (e.g., `h1` for `variant='h1'`,
 *   `p` for `variant='body1'`, `span` for `variant='caption'`). `component` replaces that element.
 * - **Styling**: Styles are applied using the `typographyVariants` utility (based on `@veraclins-dev/cva`),
 *   which defines font sizes, weights, line heights, and other properties for each variant.
 * - **Accessibility**: Heading semantics come from the rendered element. An `h1` to `h6` element is a
 *   heading at its own level whatever the variant; a `p` or `span` is never a heading.
 * - **Performance**: The component is wrapped with `memo` to prevent unnecessary re-renders when props are unchanged.
 * - **Customization**: The `className` prop allows additional styling, and the `cn` utility (from `@veraclins-dev/utils`)
 *   merges custom classes with variant-based classes.
 * - **Responsive Design**: `hero` and `h1` to `h4` step up one size at `lg` (1024px). `h5`, `h6` and the
 *   text variants keep one size, and `stat` and `stat-sm` keep their own display chain. On the variants
 *   that step, the `lg:` class sets size and line-height together, so a caller's unprefixed `text-*` or
 *   `leading-*` applies below 1024px only. Prefix the override with `lg:` to change the desktop step too.
 * - **Long words**: Every heading variant sets `overflow-wrap: break-word`, so a word with no break
 *   opportunity wraps instead of running past its container. Ordinary wrapping is unaffected.
 * - **Spacing and Truncation**: The `gutterBottom` prop adds consistent spacing below the text, and `noWrap`
 *   enables truncation for single-line text with overflow.
 * @category layouts
 */
const Typography = memo(Base);

export { Typography, type TypographyProps };

// for storybook
export default Base;
